package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.AppFirestore
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.repository.RosterCodeRepository
import com.evoapps.evoshift.domain.rosterimport.ClockRange
import com.evoapps.evoshift.domain.rosterimport.RosterCodeAction
import com.evoapps.evoshift.domain.rosterimport.RosterCodeDictionary
import com.evoapps.evoshift.domain.util.AppClock
import kotlinx.serialization.Serializable

/**
 * `workplaces/{id}/roster_import/codes` (SH-217): the codes a roster sheet uses and what each stands
 * for. A list (not a map keyed by code), since codes may hold dots or slashes. firestore.rules
 * (validRosterCodes) accept exactly these fields, at most [RosterCodeDictionary.MAX_CODES] codes.
 */
@Serializable
internal data class RosterCodeDto(
    val code: String = "",
    /** SHIFT, TEMPLATE, DAY_OFF, ABSENCE, IGNORE. */
    val action: String = "",
    val start: String? = null,
    val end: String? = null,
    val templateId: String? = null,
    val absenceType: String? = null
)

@Serializable
internal data class RosterCodesDto(
    val codes: List<RosterCodeDto> = emptyList(),
    val updatedAt: String? = null,
    val updatedBy: String? = null
)

internal fun RosterCodeDto.toDomain(): Pair<String, RosterCodeAction>? {
    val action = when (action) {
        "SHIFT" -> if (start != null && end != null) RosterCodeAction.Shift(ClockRange(start, end)) else null
        "TEMPLATE" -> templateId?.let { RosterCodeAction.Template(it) }
        "DAY_OFF" -> RosterCodeAction.DayOff
        "ABSENCE" -> AbsenceType.entries.firstOrNull { it.name == absenceType }?.let { RosterCodeAction.Absence(it) }
        "IGNORE" -> RosterCodeAction.Ignore
        else -> null
    } ?: return null
    return code to action
}

internal fun RosterCodeAction.toDto(code: String): RosterCodeDto = when (this) {
    is RosterCodeAction.Shift -> RosterCodeDto(code, "SHIFT", start = times.start, end = times.end)
    is RosterCodeAction.Template -> RosterCodeDto(code, "TEMPLATE", templateId = templateId)
    RosterCodeAction.DayOff -> RosterCodeDto(code, "DAY_OFF")
    is RosterCodeAction.Absence -> RosterCodeDto(code, "ABSENCE", absenceType = type.name)
    RosterCodeAction.Ignore -> RosterCodeDto(code, "IGNORE")
}

internal fun RosterCodesDto.toDomain() = RosterCodeDictionary(codes.mapNotNull { it.toDomain() }.toMap())

internal fun RosterCodeDictionary.toDto(updatedAt: String, updatedBy: String) = RosterCodesDto(
    codes = codes.entries
        .filter { it.key.isNotBlank() && it.key.length <= RosterCodeDictionary.MAX_CODE_LENGTH }
        .take(RosterCodeDictionary.MAX_CODES)
        .map { (code, action) -> action.toDto(code) },
    updatedAt = updatedAt,
    updatedBy = updatedBy
)

class RosterCodeRepositoryImpl(private val firestore: AppFirestore) : RosterCodeRepository {
    private fun document(workplaceId: String) =
        firestore.collection("workplaces").document(workplaceId).collection("roster_import").document("codes")

    override suspend fun get(workplaceId: String): Result<RosterCodeDictionary> = runCatching {
        val snapshot = document(workplaceId).get()
        if (!snapshot.exists) RosterCodeDictionary() else snapshot.data<RosterCodesDto>().toDomain()
    }

    override suspend fun save(workplaceId: String, dictionary: RosterCodeDictionary, updatedBy: String): Result<Unit> = runCatching {
        document(workplaceId).set(dictionary.toDto(AppClock.now().toString(), updatedBy))
    }
}
